import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  SparklesIcon,
  LightBulbIcon,
  ExclamationTriangleIcon,
  ArrowTrendingUpIcon,
  ChartBarIcon,
  BanknotesIcon,
  ClockIcon,
  FireIcon,
  TrophyIcon,
  BoltIcon,
  CheckCircleIcon,
  XMarkIcon,
  EyeIcon,
  ArrowRightIcon
} from '@heroicons/react/24/outline';

interface SmartRecommendation {
  id: string;
  type: 'cost_optimization' | 'revenue_boost' | 'efficiency' | 'risk_mitigation' | 'market_opportunity';
  title: string;
  titleAr: string;
  description: string;
  descriptionAr: string;
  impact: 'critical' | 'high' | 'medium' | 'low';
  confidence: number;
  estimatedValue: number;
  timeframe: string;
  timeframeAr: string;
  actionSteps: string[];
  actionStepsAr: string[];
  relatedPartners: string[];
  priority: number;
  urgency: 'immediate' | 'this_week' | 'this_month' | 'next_quarter';
  complexity: 'simple' | 'moderate' | 'complex';
  resources: string[];
  resourcesAr: string[];
  successProbability: number;
  marketTrend: 'rising' | 'stable' | 'declining';
}

interface SmartRecommendationsEngineProps {
  className?: string;
}

const SmartRecommendationsEngine: React.FC<SmartRecommendationsEngineProps> = ({ className = '' }) => {
  const [recommendations, setRecommendations] = useState<SmartRecommendation[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedRecommendation, setSelectedRecommendation] = useState<SmartRecommendation | null>(null);
  const [filter, setFilter] = useState<'all' | 'critical' | 'high' | 'medium' | 'low'>('all');
  const [sortBy, setSortBy] = useState<'priority' | 'value' | 'confidence'>('priority');

  useEffect(() => {
    const generateRecommendations = (): SmartRecommendation[] => {
      return [
        {
          id: '1',
          type: 'cost_optimization',
          title: 'Critical Infrastructure Cost Optimization',
          titleAr: 'تحسين حرج لتكاليف البنية التحتية',
          description: 'AI detected 67% potential savings by migrating to optimized cloud infrastructure with AWS partnership',
          descriptionAr: 'اكتشف الذكاء الاصطناعي توفير محتمل بنسبة 67% بالانتقال لبنية تحتية سحابية محسنة مع شراكة AWS',
          impact: 'critical',
          confidence: 96,
          estimatedValue: 245000,
          timeframe: '2-4 weeks implementation',
          timeframeAr: '2-4 أسابيع للتنفيذ',
          actionSteps: [
            'Conduct infrastructure audit',
            'Design migration plan',
            'Execute phased migration',
            'Monitor and optimize'
          ],
          actionStepsAr: [
            'إجراء تدقيق للبنية التحتية',
            'تصميم خطة الانتقال',
            'تنفيذ الانتقال على مراحل',
            'المراقبة والتحسين'
          ],
          relatedPartners: ['aws', 'microsoft'],
          priority: 1,
          urgency: 'immediate',
          complexity: 'complex',
          resources: ['Technical Team', 'Migration Specialist', 'Budget Approval'],
          resourcesAr: ['فريق تقني', 'أخصائي انتقال', 'موافقة الميزانية'],
          successProbability: 94,
          marketTrend: 'rising'
        },
        {
          id: '2',
          type: 'revenue_boost',
          title: 'Multi-Channel Marketing Synergy',
          titleAr: 'تناغم التسويق متعدد القنوات',
          description: 'Combining Google Ads + Snap Business + Meta Ads can increase revenue by 340% based on your target demographics',
          descriptionAr: 'دمج إعلانات جوجل + سناب للأعمال + إعلانات ميتا يمكن أن يزيد الإيرادات بنسبة 340% بناءً على فئتك المستهدفة',
          impact: 'high',
          confidence: 89,
          estimatedValue: 180000,
          timeframe: '3-6 weeks setup',
          timeframeAr: '3-6 أسابيع للإعداد',
          actionSteps: [
            'Analyze current campaign performance',
            'Design integrated campaign strategy',
            'Set up cross-platform tracking',
            'Launch coordinated campaigns',
            'Optimize based on performance data'
          ],
          actionStepsAr: [
            'تحليل أداء الحملات الحالية',
            'تصميم استراتيجية حملة متكاملة',
            'إعداد تتبع متعدد المنصات',
            'إطلاق حملات منسقة',
            'التحسين بناءً على بيانات الأداء'
          ],
          relatedPartners: ['google', 'snap', 'meta'],
          priority: 2,
          urgency: 'this_week',
          complexity: 'moderate',
          resources: ['Marketing Team', 'Creative Designer', 'Data Analyst'],
          resourcesAr: ['فريق التسويق', 'مصمم إبداعي', 'محلل بيانات'],
          successProbability: 87,
          marketTrend: 'rising'
        },
        {
          id: '3',
          type: 'efficiency',
          title: 'Automated Workflow Integration',
          titleAr: 'تكامل سير العمل الآلي',
          description: 'Microsoft 365 + Slack + Asana integration can reduce operational overhead by 45% and boost team productivity',
          descriptionAr: 'تكامل مايكروسوفت 365 + سلاك + أسانا يمكن أن يقلل التكاليف التشغيلية بنسبة 45% ويعزز إنتاجية الفريق',
          impact: 'high',
          confidence: 92,
          estimatedValue: 95000,
          timeframe: '1-2 weeks',
          timeframeAr: '1-2 أسبوع',
          actionSteps: [
            'Map current workflows',
            'Identify automation opportunities',
            'Configure integrations',
            'Train team members',
            'Monitor efficiency gains'
          ],
          actionStepsAr: [
            'رسم خرائط سير العمل الحالية',
            'تحديد فرص الأتمتة',
            'تكوين التكاملات',
            'تدريب أعضاء الفريق',
            'مراقبة مكاسب الكفاءة'
          ],
          relatedPartners: ['microsoft', 'slack', 'asana'],
          priority: 3,
          urgency: 'this_month',
          complexity: 'simple',
          resources: ['IT Team', 'Process Manager', 'Training Budget'],
          resourcesAr: ['فريق تقنية المعلومات', 'مدير العمليات', 'ميزانية التدريب'],
          successProbability: 91,
          marketTrend: 'stable'
        },
        {
          id: '4',
          type: 'market_opportunity',
          title: 'Emerging Fintech Partnership Opportunity',
          titleAr: 'فرصة شراكة ناشئة في التكنولوجيا المالية',
          description: 'Saudi fintech sector showing 280% growth - early partnership positioning can capture significant market share',
          descriptionAr: 'قطاع التكنولوجيا المالية السعودي يُظهر نمو 280% - التموضع المبكر للشراكة يمكن أن يحقق حصة سوقية كبيرة',
          impact: 'medium',
          confidence: 76,
          estimatedValue: 125000,
          timeframe: '6-12 months',
          timeframeAr: '6-12 شهر',
          actionSteps: [
            'Research fintech landscape',
            'Identify potential partners',
            'Develop partnership strategy',
            'Initiate partnership discussions',
            'Pilot program implementation'
          ],
          actionStepsAr: [
            'بحث المشهد التقني المالي',
            'تحديد الشركاء المحتملين',
            'تطوير استراتيجية الشراكة',
            'بدء مناقشات الشراكة',
            'تنفيذ برنامج تجريبي'
          ],
          relatedPartners: ['stc_pay', 'tamara', 'tabby'],
          priority: 4,
          urgency: 'next_quarter',
          complexity: 'complex',
          resources: ['Business Development', 'Legal Team', 'Strategic Planning'],
          resourcesAr: ['تطوير الأعمال', 'الفريق القانوني', 'التخطيط الاستراتيجي'],
          successProbability: 73,
          marketTrend: 'rising'
        },
        {
          id: '5',
          type: 'risk_mitigation',
          title: 'Vendor Dependency Risk Reduction',
          titleAr: 'تقليل مخاطر الاعتماد على المورد',
          description: 'Current 78% dependency on single cloud provider creates risk - diversification strategy recommended',
          descriptionAr: 'الاعتماد الحالي بنسبة 78% على مزود سحابي واحد يخلق مخاطر - استراتيجية التنويع موصى بها',
          impact: 'medium',
          confidence: 84,
          estimatedValue: 65000,
          timeframe: '3-4 months',
          timeframeAr: '3-4 أشهر',
          actionSteps: [
            'Assess current vendor dependencies',
            'Identify alternative providers',
            'Develop multi-vendor strategy',
            'Implement gradual diversification',
            'Monitor risk reduction metrics'
          ],
          actionStepsAr: [
            'تقييم التبعيات الحالية للموردين',
            'تحديد مقدمي الخدمة البديلين',
            'تطوير استراتيجية متعددة الموردين',
            'تنفيذ التنويع التدريجي',
            'مراقبة مقاييس تقليل المخاطر'
          ],
          relatedPartners: ['aws', 'azure', 'google_cloud'],
          priority: 5,
          urgency: 'this_month',
          complexity: 'moderate',
          resources: ['Risk Management', 'Technical Architecture', 'Procurement'],
          resourcesAr: ['إدارة المخاطر', 'الهندسة التقنية', 'المشتريات'],
          successProbability: 82,
          marketTrend: 'stable'
        }
      ];
    };

    setTimeout(() => {
      setRecommendations(generateRecommendations());
      setLoading(false);
    }, 1000);
  }, []);

  const filteredRecommendations = recommendations
    .filter(rec => filter === 'all' || rec.impact === filter)
    .sort((a, b) => {
      switch (sortBy) {
        case 'priority':
          return a.priority - b.priority;
        case 'value':
          return b.estimatedValue - a.estimatedValue;
        case 'confidence':
          return b.confidence - a.confidence;
        default:
          return 0;
      }
    });

  const getImpactColor = (impact: string) => {
    switch (impact) {
      case 'critical': return 'from-red-500 to-red-600';
      case 'high': return 'from-orange-500 to-orange-600';
      case 'medium': return 'from-yellow-500 to-yellow-600';
      case 'low': return 'from-green-500 to-green-600';
      default: return 'from-gray-500 to-gray-600';
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'cost_optimization': return <BanknotesIcon className="w-6 h-6" />;
      case 'revenue_boost': return <ArrowTrendingUpIcon className="w-6 h-6" />;
      case 'efficiency': return <BoltIcon className="w-6 h-6" />;
      case 'risk_mitigation': return <ExclamationTriangleIcon className="w-6 h-6" />;
      case 'market_opportunity': return <TrophyIcon className="w-6 h-6" />;
      default: return <LightBulbIcon className="w-6 h-6" />;
    }
  };

  const getTypeNameAr = (type: string) => {
    switch (type) {
      case 'cost_optimization': return 'تحسين التكاليف';
      case 'revenue_boost': return 'زيادة الإيرادات';
      case 'efficiency': return 'تحسين الكفاءة';
      case 'risk_mitigation': return 'تقليل المخاطر';
      case 'market_opportunity': return 'فرصة سوقية';
      default: return 'عام';
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('ar-SA', {
      style: 'currency',
      currency: 'SAR',
      minimumFractionDigits: 0
    }).format(amount);
  };

  if (loading) {
    return (
      <div className={`bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6 ${className}`}>
        <div className="flex items-center justify-center h-64">
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
            className="w-8 h-8 border-4 border-purple-500 border-t-transparent rounded-full"
          />
          <span className="mr-3 text-gray-600 dark:text-gray-400">
            جاري تحليل التوصيات الذكية...
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-white dark:bg-gray-800 rounded-xl shadow-lg ${className}`}>
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-500 to-pink-500 p-6 rounded-t-xl">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3 rtl:space-x-reverse">
            <SparklesIcon className="w-8 h-8 text-white" />
            <div>
              <h2 className="text-2xl font-bold text-white">محرك التوصيات الذكية</h2>
              <p className="text-purple-100">توصيات مدعومة بالذكاء الاصطناعي لتحقيق أقصى عائد</p>
            </div>
          </div>
          <div className="text-white text-right">
            <div className="text-3xl font-bold">{filteredRecommendations.length}</div>
            <div className="text-sm opacity-75">توصية نشطة</div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="p-6 border-b border-gray-200 dark:border-gray-700">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-3 sm:space-y-0">
          <div className="flex items-center space-x-3 rtl:space-x-reverse">
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value as any)}
              className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            >
              <option value="all">جميع التوصيات</option>
              <option value="critical">حرجة</option>
              <option value="high">عالية</option>
              <option value="medium">متوسطة</option>
              <option value="low">منخفضة</option>
            </select>
            
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as any)}
              className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            >
              <option value="priority">حسب الأولوية</option>
              <option value="value">حسب القيمة</option>
              <option value="confidence">حسب الثقة</option>
            </select>
          </div>
        </div>
      </div>

      {/* Recommendations List */}
      <div className="p-6 space-y-4 max-h-96 overflow-y-auto">
        {filteredRecommendations.map((recommendation, index) => (
          <motion.div
            key={recommendation.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden hover:shadow-md transition-shadow"
          >
            <div className={`bg-gradient-to-r ${getImpactColor(recommendation.impact)} p-4 text-white`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3 rtl:space-x-reverse">
                  {getTypeIcon(recommendation.type)}
                  <div>
                    <h3 className="font-bold text-lg">{recommendation.titleAr}</h3>
                    <p className="text-sm opacity-90">{getTypeNameAr(recommendation.type)}</p>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-2xl font-bold">{formatCurrency(recommendation.estimatedValue)}</div>
                  <div className="text-xs opacity-75">قيمة متوقعة</div>
                </div>
              </div>
            </div>
            
            <div className="p-4">
              <p className="text-gray-600 dark:text-gray-400 mb-3">
                {recommendation.descriptionAr}
              </p>
              
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4 rtl:space-x-reverse">
                  <span className="text-sm text-gray-500 dark:text-gray-400">
                    ثقة {recommendation.confidence}%
                  </span>
                  <span className="text-sm text-gray-500 dark:text-gray-400">
                    {recommendation.timeframeAr}
                  </span>
                </div>
                
                <button
                  onClick={() => setSelectedRecommendation(recommendation)}
                  className="flex items-center space-x-2 rtl:space-x-reverse px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                >
                  <EyeIcon className="w-4 h-4" />
                  <span>عرض التفاصيل</span>
                </button>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Detailed Recommendation Modal */}
      <AnimatePresence>
        {selectedRecommendation && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
            onClick={() => setSelectedRecommendation(null)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className={`bg-gradient-to-r ${getImpactColor(selectedRecommendation.impact)} p-6 text-white`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3 rtl:space-x-reverse">
                    {getTypeIcon(selectedRecommendation.type)}
                    <div>
                      <h2 className="text-2xl font-bold">{selectedRecommendation.titleAr}</h2>
                      <p className="opacity-90">{getTypeNameAr(selectedRecommendation.type)}</p>
                    </div>
                  </div>
                  <button
                    onClick={() => setSelectedRecommendation(null)}
                    className="text-white hover:text-gray-200 transition-colors"
                  >
                    <XMarkIcon className="w-6 h-6" />
                  </button>
                </div>
              </div>
              
              <div className="p-6 space-y-6">
                <div>
                  <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">الوصف التفصيلي</h3>
                  <p className="text-gray-600 dark:text-gray-400">{selectedRecommendation.descriptionAr}</p>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                    <div className="text-sm text-gray-600 dark:text-gray-400">القيمة المتوقعة</div>
                    <div className="text-2xl font-bold text-green-600">{formatCurrency(selectedRecommendation.estimatedValue)}</div>
                  </div>
                  <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                    <div className="text-sm text-gray-600 dark:text-gray-400">مستوى الثقة</div>
                    <div className="text-2xl font-bold text-blue-600">{selectedRecommendation.confidence}%</div>
                  </div>
                </div>
                
                <div>
                  <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-3">خطوات التنفيذ</h3>
                  <div className="space-y-2">
                    {selectedRecommendation.actionStepsAr.map((step, index) => (
                      <div key={index} className="flex items-center space-x-3 rtl:space-x-reverse">
                        <div className="w-6 h-6 bg-purple-600 text-white rounded-full flex items-center justify-center text-sm font-bold">
                          {index + 1}
                        </div>
                        <span className="text-gray-700 dark:text-gray-300">{step}</span>
                      </div>
                    ))}
                  </div>
                </div>
                
                <div>
                  <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-3">الموارد المطلوبة</h3>
                  <div className="flex flex-wrap gap-2">
                    {selectedRecommendation.resourcesAr.map((resource, index) => (
                      <span
                        key={index}
                        className="px-3 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-400 rounded-full text-sm"
                      >
                        {resource}
                      </span>
                    ))}
                  </div>
                </div>
                
                <div className="flex items-center justify-end space-x-3 rtl:space-x-reverse pt-4 border-t border-gray-200 dark:border-gray-600">
                  <button
                    onClick={() => setSelectedRecommendation(null)}
                    className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 transition-colors"
                  >
                    إغلاق
                  </button>
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                  >
                    بدء التنفيذ
                  </motion.button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default SmartRecommendationsEngine;
